/**
 * useCollaboration hook
 * Convenience hook for accessing collaborative editing state
 */

import { useEffect, useCallback } from 'react';
import { useCollaboration as useCollaborationContext } from '../contexts/CollaborativeContext';

interface UseDocumentCollaborationOptions {
  docId: string;
  userId: string;
  userName: string;
  enabled?: boolean;
}

/**
 * Hook for document-specific collaboration
 * Automatically connects/disconnects based on document ID
 */
export function useDocumentCollaboration({
  docId,
  userId,
  userName,
  enabled = true,
}: UseDocumentCollaborationOptions) {
  const {
    ydoc,
    provider,
    awareness,
    isConnected,
    isConnecting,
    isSynced,
    connectionError,
    connectedUsers,
    connect,
    disconnect,
  } = useCollaborationContext();

  // Connect when docId changes
  useEffect(() => {
    if (enabled && docId && userId && userName) {
      connect(docId, userId, userName);
    }

    return () => {
      if (enabled) {
        disconnect();
      }
    };
  }, [docId, userId, userName, enabled, connect, disconnect]);

  // Update cursor position in awareness
  const updateCursor = useCallback(
    (index: number, length: number = 0) => {
      if (awareness) {
        awareness.setLocalStateField('cursor', { index, length });
      }
    },
    [awareness]
  );

  // Clear cursor (e.g., when leaving editor)
  const clearCursor = useCallback(() => {
    if (awareness) {
      awareness.setLocalStateField('cursor', null);
    }
  }, [awareness]);

  return {
    ydoc,
    provider,
    awareness,
    isConnected,
    isConnecting,
    isSynced,
    connectionError,
    connectedUsers,
    updateCursor,
    clearCursor,
  };
}

export default useDocumentCollaboration;
